import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Drawer,
  Grid,
  Group,
  Image,
  Loader,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from "../../../utils/notify";
import {
  IconAlertCircle,
  IconBuilding,
  IconCheck,
  IconDeviceFloppy,
  IconHistory,
  IconTrash,
  IconUser,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import {
  ClientForm,
  HistoryEntry,
  FormTemplateField,
  FieldActor,
  ImageUploadFieldValue,
  SoftDeleteFormImagePayload,
} from "../../../types/formTemplates";
import {
  coerceImageUploadField,
  readPointerDocumentUuid,
} from "../../../utils/formImagePointer";
import { API_CONFIG, getApiUrl } from "../../../config/api";
import { FieldRenderer } from "../FieldRenderer";
import { PaginatedShell } from "../PaginatedShell";
import { ImageUploadField } from "./ImageUploadField";
import { SignaturePad } from "./SignaturePad";
import { FormHistoryTimeline } from "./FormHistoryTimeline";
import { useClientForms } from "../../../context/ClientFormsContext";
import { useApp } from "../../../context/AppContext";
import {
  collectPhiReadItems,
  dataUrlToFile,
  readFormUploadDisplayUrls,
  uploadFieldValue,
} from "../../../utils/formUpload";
import {
  stripLayoutFieldValues,
  validateAnswers,
} from "../../../utils/answerValidation";
import { ACTOR_COLOR, ACTOR_LABEL } from "../constants";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";

export interface FormViewerProps {
  salonId: string;
  clientId: string;
  formId: string;
  onClose: () => void;
  onChanged?: () => void;
}

type ActorFilter = "all" | "staff" | "client";

function isLayoutField(f: FormTemplateField): boolean {
  return (
    f.type === "section_header" ||
    f.type === "section_description" ||
    f.type === "divider" ||
    f.type === "page_break"
  );
}

/** Split a list of fields into pages at each `page_break` field. */
function splitIntoPages(fields: FormTemplateField[]): FormTemplateField[][] {
  const pages: FormTemplateField[][] = [[]];
  for (const f of fields) {
    if (f.type === "page_break") {
      pages.push([]);
    } else {
      pages[pages.length - 1].push(f);
    }
  }
  return pages.length === 0 ? [[]] : pages;
}

function ActorBadge({ actor }: { actor: FieldActor | null }) {
  if (!actor) return null;
  return (
    <Badge
      size="xs"
      variant="light"
      color={ACTOR_COLOR[actor]}
      style={{ textTransform: "none" }}
    >
      {ACTOR_LABEL[actor]}
    </Badge>
  );
}

export const FormViewer: React.FC<FormViewerProps> = ({
  salonId,
  clientId,
  formId,
  onClose,
  onChanged,
}) => {
  const {
    getClientForm,
    saveDraft,
    submitForm,
    deleteClientForm,
    markFormOpened,
    renameClientForm,
  } = useClientForms();
  const { fetchWithAuth } = useApp();

  const [form, setForm] = useState<ClientForm | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [actorFilter, setActorFilter] = useState<ActorFilter>("all");
  const [pageIndex, setPageIndex] = useState(0);
  /** Presigned GET URLs keyed by s3_key (phi/…). */
  const [urlByS3Key, setUrlByS3Key] = useState<Record<string, string>>({});
  const [uploadReadLoading, setUploadReadLoading] = useState(false);
  const [uploadReadError, setUploadReadError] = useState<string | null>(null);
  /** When true, show signature pad instead of saved image for that field. */
  const [signatureReplacing, setSignatureReplacing] = useState<
    Record<string, boolean>
  >({});
  // Local edit buffer for the form-name input rendered in the header. Seeded
  // from custom_name (the staff-set override) or falls back to the template
  // name. Commits via renameClientForm on blur / Enter; Escape reverts.
  const [nameDraft, setNameDraft] = useState("");
  const [renamingTitle, setRenamingTitle] = useState(false);

  /** Signatures (and file uploads) only land in `data` after S3 upload finishes. */
  const inflightUploadCountRef = useRef(0);
  // Monotonic token bumped before every runSave dispatch. A response
  // whose token has been superseded by a newer dispatch must not
  // overwrite local state — otherwise rapid markup saves / soft-deletes
  // can land in flight in one order and resolve in another, snapping
  // the form back to a stale snapshot.
  const saveTokenRef = useRef(0);

  const waitForInflightUploads = useCallback(async (): Promise<boolean> => {
    const deadline = Date.now() + 60_000;
    while (inflightUploadCountRef.current > 0 && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 50));
    }
    return inflightUploadCountRef.current === 0;
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getClientForm(clientId, formId);
      setForm(response.form);
      setHistory(response.history);
      setData(response.form.data ?? {});
      // Fire-and-forget: mark this form as opened by staff. Backend is
      // idempotent + audits only on the call that actually flips, so this
      // is safe to call on every load.
      if (!response.form.is_opened && !response.form.is_submitted) {
        setForm((f) => (f ? { ...f, is_opened: true } : f));
        markFormOpened(clientId, formId).catch((err) => {
          console.warn("Failed to mark form as opened:", err);
        });
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Could not load form.");
    } finally {
      setIsLoading(false);
    }
  }, [clientId, formId, getClientForm, markFormOpened]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the rename TextInput's local buffer in sync with whatever name the
  // form currently has on the server (template name or staff override).
  useEffect(() => {
    if (form) {
      setNameDraft(form.custom_name ?? form.name);
    }
  }, [form?.custom_name, form?.name]);

  const commitRename = useCallback(async () => {
    if (!form) return;
    const trimmed = nameDraft.trim();
    const current = form.custom_name ?? form.name;
    // No-op when nothing actually changed or the user blanked the field.
    if (trimmed === "" || trimmed === current) {
      setNameDraft(current);
      return;
    }
    // Reverting back to the template default sends null so the override is
    // cleared rather than persisted as a duplicate of `name`.
    const customName = trimmed === form.name ? null : trimmed;
    setRenamingTitle(true);
    try {
      const updated = await renameClientForm(clientId, formId, customName);
      setForm((prev) =>
        prev ? { ...prev, custom_name: updated.custom_name ?? null } : prev,
      );
      onChanged?.();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not rename form",
      });
      setNameDraft(current);
    } finally {
      setRenamingTitle(false);
    }
  }, [form, nameDraft, renameClientForm, clientId, formId, onChanged]);

  const schema = form?.schema_snapshot;
  // State-based editability for the whole form:
  // - draft, awaiting_staff: staff can edit. In awaiting_staff the client
  //   already submitted, so client-actor fields are individually locked
  //   below (their answers + signatures are immutable post-submit).
  // - awaiting_client, complete: fully read-only. Either the staff already
  //   submitted (awaiting_client) or the form is terminal (complete).
  // The legacy `is_submitted` boolean is the back-compat read fallback.
  const formState = form?.state;
  const readOnly = formState
    ? formState === "awaiting_client" || formState === "complete"
    : Boolean(form?.is_submitted);
  // In draft state staff can fill any field — including client-actor
  // fields on behalf of a client who can't access the portal. When the
  // form is `awaiting_staff` the client has already submitted their
  // portion and those fields lock individually.
  const isFieldReadOnly = (field: FormTemplateField): boolean => {
    if (readOnly) return true;
    if (formState === "awaiting_staff" && field.actor === "client") return true;
    return false;
  };

  const pages = useMemo(
    () => (schema ? splitIntoPages(schema.fields) : [[]]),
    [schema],
  );
  /**
   * Filter each page by the active actor view. Pages keep their indexes so
   * page N stays page N even when filtering to Staff or Client only — just
   * with an "empty for this view" message when nothing matches.
   */
  const filteredPages = useMemo(
    () =>
      pages.map((pageFields) =>
        pageFields.filter((f) => {
          if (isLayoutField(f)) return true;
          if (actorFilter === "all") return true;
          return f.actor === actorFilter;
        }),
      ),
    [pages, actorFilter],
  );
  const totalPages = filteredPages.length;

  const phiReadItems = useMemo(() => {
    const fields = schema?.fields;
    if (!fields?.length) return [];
    return collectPhiReadItems(fields, data);
  }, [schema, data]);

  const phiReadFingerprint = useMemo(
    () => JSON.stringify(phiReadItems),
    [phiReadItems],
  );

  const refreshUploadDisplayUrls = useCallback(async () => {
    if (!schema?.fields?.length || !salonId || !clientId || !formId) {
      setUrlByS3Key({});
      setUploadReadError(null);
      return;
    }
    const items = collectPhiReadItems(schema.fields, data);
    if (items.length === 0) {
      setUrlByS3Key({});
      setUploadReadError(null);
      return;
    }
    setUploadReadLoading(true);
    setUploadReadError(null);
    try {
      const map = await readFormUploadDisplayUrls(
        fetchWithAuth,
        salonId,
        clientId,
        formId,
        items,
      );
      const next: Record<string, string> = {};
      map.forEach((meta, s3Key) => {
        next[s3Key] = meta.url;
      });
      setUrlByS3Key(next);
    } catch (err) {
      setUrlByS3Key({});
      setUploadReadError(
        err instanceof Error ? err.message : "Could not load uploaded files.",
      );
    } finally {
      setUploadReadLoading(false);
    }
  }, [schema, data, salonId, clientId, formId, fetchWithAuth]);

  useEffect(() => {
    void refreshUploadDisplayUrls();
  }, [phiReadFingerprint, refreshUploadDisplayUrls]);

  useEffect(() => {
    if (phiReadItems.length === 0) return;
    const intervalMs = 8 * 60 * 1000;
    const id = window.setInterval(() => {
      void refreshUploadDisplayUrls();
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [phiReadItems.length, phiReadFingerprint, refreshUploadDisplayUrls]);

  useEffect(() => {
    // New form: first page; clear signature "replace" UI state.
    setPageIndex(0);
    setSignatureReplacing({});
  }, [formId]);

  useEffect(() => {
    // If pages shrink (e.g. a page_break is removed server-side), clamp.
    if (pageIndex > totalPages - 1) {
      setPageIndex(Math.max(0, totalPages - 1));
    }
  }, [totalPages, pageIndex]);

  const setValue = useCallback((fieldId: string, value: unknown) => {
    setData((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  /**
   * Signature strokes stay as data URLs in state until Save now / Submit.
   * Then we upload to S3 and replace with phi/… keys (same as image_upload).
   */
  const resolveSignatureDataUrlsInData = useCallback(
    async (base: Record<string, unknown>): Promise<Record<string, unknown>> => {
      if (!schema) return base;
      let next = { ...base };
      let changed = false;
      for (const field of schema.fields) {
        if (field.type !== "signature") continue;
        const v = next[field.id];
        if (typeof v === "string" && v.startsWith("data:image")) {
          inflightUploadCountRef.current += 1;
          setUploading((u) => ({ ...u, [field.id]: true }));
          try {
            const file = dataUrlToFile(v, `${field.id}-signature.png`);
            const s3Key = await uploadFieldValue({
              fetchWithAuth,
              salonId,
              clientId,
              formId,
              fieldId: field.id,
              file,
            });
            next[field.id] = s3Key;
            changed = true;
          } finally {
            inflightUploadCountRef.current -= 1;
            setUploading((u) => ({ ...u, [field.id]: false }));
          }
        }
      }
      if (changed) setData(next);
      return next;
    },
    [schema, fetchWithAuth, salonId, clientId, formId],
  );

  const runSave = useCallback(
    async (nextData: Record<string, unknown>) => {
      if (!schema) return;
      if (readOnly) return;

      // Reserve a token. A later runSave will bump the counter; when
      // this dispatch's response lands we'll compare and skip the
      // setData if a newer call has already superseded us.
      saveTokenRef.current += 1;
      const myToken = saveTokenRef.current;

      let toPersist = nextData;
      try {
        toPersist = await resolveSignatureDataUrlsInData(nextData);
      } catch (err) {
        notifyError(err instanceof Error ? err.message : "Unknown error.", {
          title: "Could not upload signature",
        });
        return;
      }

      const stripped = stripLayoutFieldValues(schema, toPersist);

      try {
        const response = await saveDraft(clientId, formId, stripped);
        // Only commit the response if we're still the latest save in
        // flight — otherwise a newer save has more current state and
        // we must NOT overwrite it.
        if (saveTokenRef.current !== myToken) return;
        if (response?.form) {
          setForm(response.form);
          setData(response.form.data ?? {});
        } else {
          try {
            const d = await getClientForm(clientId, formId);
            if (saveTokenRef.current !== myToken) return;
            if (d.form) {
              setForm(d.form);
              setData(d.form.data ?? {});
              setHistory(d.history);
            }
          } catch {
            /* non-fatal */
          }
        }
        if (onChanged) onChanged();
      } catch (err) {
        notifyError(err instanceof Error ? err.message : "Unknown error.", {
          title: "Could not save draft",
        });
      }
    },
    [
      clientId,
      formId,
      schema,
      readOnly,
      saveDraft,
      getClientForm,
      onChanged,
      resolveSignatureDataUrlsInData,
    ],
  );

  // Memoize so ImageUploadField's openLightbox / handleMarkupSave /
  // handleLoadMarkupVersions useCallback deps stay stable across
  // renders. clientDisplayName isn't on FormViewer's props today —
  // when it is, plug it in here and the ChartEditor header subtitle
  // + Info drawer "Uploaded By" fallback both light up automatically.
  const markupContext = useMemo(
    () => ({ salonId, clientId }),
    [salonId, clientId],
  );

  const handleUpload = useCallback(
    async (fieldId: string, file: File, append: boolean) => {
      if (!form) return;
      inflightUploadCountRef.current += 1;
      setUploading((u) => ({ ...u, [fieldId]: true }));
      try {
        // image_upload fields use the pointer shape so the backend
        // dual-write materializer can stamp salon_document_uuid + record
        // file metadata for the Documents tab Info drawer.
        const uploaded = await uploadFieldValue({
          fetchWithAuth,
          salonId,
          clientId,
          formId,
          fieldId,
          file,
          asPointer: true,
        });
        setData((prev) => {
          let nextValue: unknown;
          if (append) {
            const existing = Array.isArray(prev[fieldId])
              ? (prev[fieldId] as unknown[])
              : prev[fieldId] != null
                ? [prev[fieldId] as unknown]
                : [];
            nextValue = [...existing, uploaded];
          } else {
            nextValue = uploaded;
          }
          const next = { ...prev, [fieldId]: nextValue };
          return next;
        });
      } catch (err) {
        notifyError(err instanceof Error ? err.message : "Unknown error.", {
          title: "Upload failed",
        });
      } finally {
        inflightUploadCountRef.current -= 1;
        setUploading((u) => ({ ...u, [fieldId]: false }));
      }
    },
    [form, fetchWithAuth, salonId, clientId, formId],
  );

  const handleSignatureChange = useCallback(
    (field: FormTemplateField, dataUrl: string | null) => {
      setValue(field.id, dataUrl);
      if (dataUrl) {
        setSignatureReplacing((prev) => ({ ...prev, [field.id]: false }));
      }
    },
    [setValue],
  );

  const submit = async () => {
    if (!schema || !form) return;

    const uploadsDone = await waitForInflightUploads();
    if (!uploadsDone) {
      notifyError("Wait for file uploads to finish, then try again.", {
        title: "Uploads still in progress",
      });
      return;
    }

    let toValidate = data;
    try {
      toValidate = await resolveSignatureDataUrlsInData(data);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not upload signature",
      });
      return;
    }

    // Client-side strict validation to give inline errors without a
    // round-trip. Backend will still be the source of truth. Scope the
    // required check to staff-actor fields only — a staff submit on a
    // mixed-actor draft is allowed to leave client-required fields
    // empty (backend transitions form to awaiting_client so the client
    // can finish via portal). When staff is filling on-behalf and has
    // filled the client-required fields too, backend recognizes it and
    // transitions straight to complete.
    const stripped = stripLayoutFieldValues(schema, toValidate);
    const result = validateAnswers({
      schema,
      data: stripped,
      strict: true,
      requiredActor: "staff",
      salonUuid: salonId,
      clientUuid: clientId,
    });
    if (!result.ok) {
      setFieldErrors(result.errorsByFieldId);
      setSubmitAttempted(true);
      notifyError(result.errors[0]?.message ?? "Form is not complete.", {
        title: "Fix validation errors",
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await submitForm(clientId, formId, stripped);
      const submittedLabel = response?.form?.name ?? form?.name ?? "Form";
      if (response?.form) {
        setForm(response.form);
      }
      // Reload canonical form + history (handles submit responses that omit `form`).
      await load();
      notifySuccess(`${submittedLabel} is now locked.`, {
        title: "Form submitted",
      });
      if (onChanged) onChanged();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not submit form",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!form) return;
    const isDraft = form.state ? form.state === "draft" : !form.is_submitted;
    if (!isDraft) return;
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!form) return;
    const isDraft = form.state ? form.state === "draft" : !form.is_submitted;
    if (!isDraft) return;
    setIsDeleting(true);
    try {
      await deleteClientForm(clientId, formId);
      notifySuccess("Removed from this client.", { title: "Form deleted" });
      setDeleteOpen(false);
      if (onChanged) onChanged();
      onClose();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not delete form",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const anyFieldUploading = useMemo(
    () => Object.values(uploading).some(Boolean),
    [uploading],
  );

  return (
    <Modal
      opened
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      styles={{ body: { padding: 0, height: "100dvh", overflow: "hidden" } }}
    >
      <Stack gap={0} style={{ height: "100dvh" }}>
        <Group
          justify="space-between"
          align="center"
          px="md"
          py="sm"
          style={{
            borderBottom: "1px solid var(--mantine-color-gray-2)",
            background: "var(--mantine-color-white)",
            flexShrink: 0,
          }}
        >
          <Group gap="md" align="center">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconX size={16} />}
              onClick={onClose}
            >
              Close
            </Button>
            <div>
              {/* While the form is editable, the title is an inline
                  TextInput mirroring FormBuilder.tsx so staff can rename
                  here instead of from the client-side list. Submitted /
                  terminal forms render the static Title. */}
              {form && !readOnly ? (
                <TextInput
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.currentTarget.value)}
                  onBlur={() => void commitRename()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      (e.currentTarget as HTMLInputElement).blur();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setNameDraft(form.custom_name ?? form.name);
                      (e.currentTarget as HTMLInputElement).blur();
                    }
                  }}
                  disabled={renamingTitle}
                  size="md"
                  styles={{ input: { fontWeight: 600, fontSize: 16 } }}
                  w={320}
                  aria-label="Form name"
                />
              ) : (
                <Title order={5}>
                  {form?.custom_name ?? form?.name ?? "Loading…"}
                </Title>
              )}
              <Text size="xs" c="dimmed">
                {form ? (
                  <>
                    v{form.source_version} •{" "}
                    {form.schema_snapshot.fields.length} fields
                  </>
                ) : (
                  "Loading…"
                )}
              </Text>
            </div>
            {readOnly && (
              <Badge variant="light" color="teal">
                Submitted on{" "}
                {form?.submitted_at
                  ? new Date(form.submitted_at).toLocaleDateString()
                  : ""}
              </Badge>
            )}
            {readOnly && form?.expires_at && (
              <Badge
                variant="light"
                color="red.4"
                style={{ textTransform: "none" }}
              >
                Expires {new Date(form.expires_at).toLocaleDateString()}
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconHistory size={16} />}
              onClick={async () => {
                setHistoryOpen(true);
                try {
                  const d = await getClientForm(clientId, formId);
                  setHistory(d.history);
                } catch {
                  /* non-fatal — keep last loaded history */
                }
              }}
            >
              History
            </Button>
            {!readOnly && form && (
              <Button
                variant="outline"
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
            {!readOnly && (
              <>
                <Button
                  color="var(--brand-teal)"
                  variant="outline"
                  leftSection={<IconDeviceFloppy size={16} />}
                  disabled={anyFieldUploading}
                  onClick={async () => {
                    const done = await waitForInflightUploads();
                    if (!done) {
                      notifyWarning(
                        "Wait for uploads to finish before saving.",
                        { title: "Uploads in progress" },
                      );
                      return;
                    }
                    runSave(data);
                  }}
                >
                  Save now
                </Button>
                <Button
                  color="var(--brand-teal)"
                  leftSection={<IconCheck size={16} />}
                  onClick={submit}
                  loading={submitting}
                  disabled={anyFieldUploading}
                >
                  {formState === "awaiting_staff"
                    ? "Submit staff section"
                    : "Submit form"}
                </Button>
              </>
            )}
          </Group>
        </Group>

        <Box
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: 24,
            background: "var(--mantine-color-gray-0)",
          }}
        >
          {loadError && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              maw={800}
              mx="auto"
              mb="md"
            >
              {loadError}
            </Alert>
          )}
          {isLoading ? (
            <Group justify="center" p="xl">
              <Loader />
            </Group>
          ) : form && schema ? (
            (() => {
              const renderFieldCol = (field: FormTemplateField) => {
                const span = field.layout?.colSpan ?? 12;
                const colSpan = span === 6 ? 6 : 12;
                const fieldError = submitAttempted
                  ? fieldErrors[field.id]
                  : undefined;

                if (isLayoutField(field)) {
                  return (
                    <Grid.Col key={field.id} span={{ base: 12, sm: colSpan }}>
                      <FieldRenderer field={field} mode="preview" />
                    </Grid.Col>
                  );
                }

                if (field.type === "signature") {
                  const raw = data[field.id];
                  const s3Key =
                    typeof raw === "string" && raw.startsWith("phi/")
                      ? raw
                      : null;
                  const dataUrl =
                    typeof raw === "string" && raw.startsWith("data:image")
                      ? raw
                      : null;
                  const signedUrl = s3Key ? urlByS3Key[s3Key] : undefined;
                  const replacing = Boolean(signatureReplacing[field.id]);
                  const showSavedImage = Boolean(
                    s3Key && signedUrl && !replacing && !dataUrl,
                  );
                  const showPad = !showSavedImage;

                  return (
                    <Grid.Col key={field.id} span={{ base: 12, sm: colSpan }}>
                      <Stack gap={4}>
                        <Group gap="xs">
                          <Text size="sm" fw={500}>
                            {field.label}
                            {field.required && (
                              <Text span c="red" ml={4}>
                                *
                              </Text>
                            )}
                          </Text>
                          <ActorBadge actor={field.actor} />
                        </Group>
                        {uploadReadLoading &&
                          s3Key &&
                          !signedUrl &&
                          !replacing &&
                          !dataUrl && (
                            <Group gap="xs">
                              <Loader size="xs" />
                              <Text size="xs" c="dimmed">
                                Loading signature…
                              </Text>
                            </Group>
                          )}
                        {uploadReadError &&
                          s3Key &&
                          !signedUrl &&
                          !replacing &&
                          !dataUrl && (
                            <Text size="xs" c="red">
                              {uploadReadError}
                            </Text>
                          )}
                        {showSavedImage && (
                          <Stack gap="xs">
                            <Image
                              src={signedUrl}
                              alt={`Saved signature for ${field.label}`}
                              radius="md"
                              fit="contain"
                              mah={180}
                              style={{ maxWidth: "100%" }}
                            />
                            {!isFieldReadOnly(field) && (
                              <Button
                                variant="light"
                                size="xs"
                                onClick={() => {
                                  setValue(field.id, null);
                                  setSignatureReplacing((prev) => ({
                                    ...prev,
                                    [field.id]: true,
                                  }));
                                }}
                              >
                                Replace signature
                              </Button>
                            )}
                          </Stack>
                        )}
                        {showPad && (
                          <SignaturePad
                            value={dataUrl}
                            onChange={(url) =>
                              handleSignatureChange(field, url)
                            }
                            disabled={
                              isFieldReadOnly(field) || uploading[field.id]
                            }
                          />
                        )}
                        {uploading[field.id] && (
                          <Text size="xs" c="dimmed">
                            Uploading signature…
                          </Text>
                        )}
                        {typeof data[field.id] === "string" &&
                          (data[field.id] as string).startsWith(
                            "data:image",
                          ) && (
                            <Text size="xs" c="dimmed">
                              Stored in this session only — uploads when you
                              save or submit.
                            </Text>
                          )}
                        {fieldError && (
                          <Text size="xs" c="red">
                            {fieldError}
                          </Text>
                        )}
                      </Stack>
                    </Grid.Col>
                  );
                }

                if (field.type === "image_upload") {
                  const existing = coerceImageUploadField(
                    data[field.id] as ImageUploadFieldValue,
                  );
                  const cfg = field.config as {
                    maxFiles?: number | null;
                  };
                  return (
                    <Grid.Col key={field.id} span={{ base: 12, sm: colSpan }}>
                      <ImageUploadField
                        label={field.label}
                        required={field.required}
                        isReadOnly={isFieldReadOnly(field)}
                        maxFiles={cfg.maxFiles}
                        existing={existing}
                        uploading={Boolean(uploading[field.id])}
                        uploadReadLoading={uploadReadLoading}
                        urlByS3Key={urlByS3Key}
                        errorText={fieldError}
                        actorBadge={<ActorBadge actor={field.actor} />}
                        markupContext={markupContext}
                        onPointerUpdate={(entry, updates) => {
                          // Read the freshest data via setData((prev) =>
                          // ...) so back-to-back pointer patches (rapid
                          // markup save + soft-delete, two markup saves
                          // on different entries) can't drop each other
                          // by computing from a stale closure snapshot.
                          // Match by object identity first; fall back to
                          // salon_document_uuid equality so a re-fetched
                          // data array (different object refs) still
                          // patches the right entry.
                          let newData: Record<string, unknown> = {};
                          setData((prev) => {
                            const cur = coerceImageUploadField(
                              prev[field.id] as ImageUploadFieldValue,
                            );
                            const next = cur.map((e) => {
                              const identityMatch = e === entry;
                              const uuidMatch =
                                typeof e === "object" &&
                                e !== null &&
                                typeof entry === "object" &&
                                entry !== null &&
                                "salon_document_uuid" in e &&
                                "salon_document_uuid" in entry &&
                                e.salon_document_uuid ===
                                  entry.salon_document_uuid &&
                                !!e.salon_document_uuid;
                              if (!identityMatch && !uuidMatch) return e;
                              if (typeof e === "string") {
                                return { s3_key: e, ...updates };
                              }
                              return { ...e, ...updates };
                            });
                            newData = { ...prev, [field.id]: next };
                            return newData;
                          });
                          void runSave(newData);
                        }}
                        onUpload={(file, append) =>
                          handleUpload(field.id, file, append)
                        }
                        onRemove={async (entry) => {
                          const docUuid = readPointerDocumentUuid(entry);
                          if (docUuid) {
                            // Pointer with backend row → soft-delete
                            // server-side. Backend tombstones both
                            // salon_documents and data_json in one txn;
                            // we still patch local state so the
                            // carousel updates immediately.
                            try {
                              await fetchWithAuth(
                                getApiUrl(
                                  API_CONFIG.ENDPOINTS
                                    .CLIENT_FORM_IMAGE_SOFT_DELETE,
                                  "",
                                  {
                                    salonId,
                                    clientId,
                                    formId,
                                    fieldId: field.id,
                                  },
                                ),
                                {
                                  method: "POST",
                                  body: JSON.stringify({
                                    salon_document_uuid: docUuid,
                                  } satisfies SoftDeleteFormImagePayload),
                                },
                              );
                            } catch (err) {
                              notifyError(
                                err instanceof Error
                                  ? err.message
                                  : "Unknown error.",
                                { title: "Could not remove image" },
                              );
                              return;
                            }
                          }
                          // Patch local state: tombstone the pointer so
                          // backend + frontend agree without a refetch.
                          setData((prev) => {
                            const cur = coerceImageUploadField(
                              prev[field.id] as ImageUploadFieldValue,
                            );
                            const next = cur.map((e) => {
                              if (e !== entry) return e;
                              if (typeof e === "string") return null;
                              return {
                                ...e,
                                deleted_at: new Date().toISOString(),
                              };
                            });
                            return {
                              ...prev,
                              [field.id]: next.filter((e) => e !== null),
                            };
                          });
                        }}
                      />
                    </Grid.Col>
                  );
                }

                return (
                  <Grid.Col key={field.id} span={{ base: 12, sm: colSpan }}>
                    <FieldRenderer
                      field={field}
                      mode={isFieldReadOnly(field) ? "preview" : "edit"}
                      value={data[field.id]}
                      onChange={(v) => setValue(field.id, v)}
                      error={fieldError}
                    />
                  </Grid.Col>
                );
              };

              const pageNodes = filteredPages.map((pageFields, i) => (
                <Box key={`page-${i}`}>
                  <Grid gutter="md">
                    {pageFields.length > 0 ? (
                      pageFields.map(renderFieldCol)
                    ) : (
                      <Grid.Col span={12}>
                        <Text c="dimmed" size="sm" ta="center">
                          Nothing on this page for the selected view.
                        </Text>
                      </Grid.Col>
                    )}
                  </Grid>
                </Box>
              ));

              return (
                <PaginatedShell
                  pages={pageNodes}
                  activeIndex={pageIndex}
                  onChange={setPageIndex}
                  contentStyle={{
                    padding: 24,
                    background: "var(--mantine-color-white)",
                    borderRadius: 12,
                    border: "1px solid var(--mantine-color-gray-2)",
                  }}
                  header={
                    <Group
                      justify="space-between"
                      align="center"
                      mb="md"
                      wrap="wrap"
                    >
                      <Title order={3}>{form.name}</Title>
                      <SegmentedControl
                        size="xs"
                        value={actorFilter}
                        onChange={(v) => setActorFilter(v as ActorFilter)}
                        data={[
                          {
                            value: "all",
                            label: (
                              <Group gap={4} wrap="nowrap">
                                <IconUsers size={12} /> All
                              </Group>
                            ),
                          },
                          {
                            value: "staff",
                            label: (
                              <Group gap={4} wrap="nowrap">
                                <IconBuilding size={12} /> Staff
                              </Group>
                            ),
                          },
                          {
                            value: "client",
                            label: (
                              <Group gap={4} wrap="nowrap">
                                <IconUser size={12} /> Client
                              </Group>
                            ),
                          },
                        ]}
                        aria-label="Filter by actor"
                      />
                    </Group>
                  }
                />
              );
            })()
          ) : null}
        </Box>
      </Stack>

      <Drawer
        opened={historyOpen}
        onClose={() => setHistoryOpen(false)}
        position="right"
        title={<Text fw={700}>Form history</Text>}
        size="sm"
      >
        <FormHistoryTimeline
          history={history}
          fields={form?.schema_snapshot?.fields}
        />
      </Drawer>

      <DeleteConfirmationModal
        isOpen={deleteOpen}
        onClose={() => {
          if (!isDeleting) setDeleteOpen(false);
        }}
        onConfirm={confirmDelete}
        title="Delete form"
        message={form ? `Delete "${form.name}"?\n\nThis cannot be undone.` : ""}
        isLoading={isDeleting}
      />
    </Modal>
  );
};
